import React, { useEffect, useMemo, useState } from 'react'
import {
  Wrapper,
  InputWrapper,
  WrapperBackButton
} from '../RecoverAccess.styles'
import { Button, ButtonEllipse, Input, Title } from '@arvoreeducacao/bonsai-ds'
import { useHistory, useLocation } from 'react-router-dom'
import { LOGIN_PATH } from 'routes/paths'
import useToast from 'utils/hooks/use-toast'
import {
  usePasswordResetMutation,
  useValidatePasswordResetMutation
} from '__gql-generated__/types'
import PasswordValidation from './PasswordValidation'
import { useTranslation } from 'i18n'

const RecoverPassword = () => {
  const { t } = useTranslation()
  const [password, setPassword] = useState('')
  const [passwordConfirmation, setPasswordConfirmation] = useState('')
  const [passwordError, setPasswordError] = useState(false)
  const [submitLoading, setSubmitLoading] = useState(false)
  const { toastMessage } = useToast()
  const history = useHistory()

  const [validatePasswordReset] = useValidatePasswordResetMutation()
  const [passwordReset] = usePasswordResetMutation()

  const location = useLocation()
  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location]
  )

  const userEmail = queryParams.get('email')
  const resetToken = queryParams.get('token')

  useEffect(() => {
    const validate = async () => {
      try {
        await validatePasswordReset({
          variables: {
            email: userEmail,
            token: resetToken
          }
        })
      } catch {
        history.push(LOGIN_PATH)
      }
    }

    validate()
  }, [validatePasswordReset, userEmail, resetToken, history])

  const handleChangeInput = (
    value: string,
    setValue: (value: string) => void
  ) => {
    setValue(value)
    setPasswordError(false)
  }

  const handleSubmit = async () => {
    if (password !== passwordConfirmation) {
      setPasswordError(true)
      return
    }

    try {
      setSubmitLoading(true)

      await passwordReset({
        variables: {
          email: userEmail,
          token: resetToken,
          newPassword: password
        }
      })

      toastMessage({
        message: t('Senha alterada com sucesso'),
        type: 'success'
      })

      history.push(LOGIN_PATH)
    } catch {
      toastMessage({
        message: t('Falha ao alterar a senha'),
        type: 'error'
      })
    } finally {
      setSubmitLoading(false)
    }
  }

  return (
    <Wrapper>
      <WrapperBackButton>
        <ButtonEllipse
          iconName="arrow-left"
          type="primary"
          onClick={() => {
            history.push(LOGIN_PATH)
          }}
          aria-label="Voltar para login"
        />
      </WrapperBackButton>
      <InputWrapper>
        <Title
          text={t('Por favor, cadastre sua nova senha!')}
          size={3}
          style={{ padding: '32px 0 24px', textAlign: 'center' }}
        />
        <Input
          label={t('Nova senha')}
          type="password"
          value={password}
          onChange={e => handleChangeInput(e.target.value, setPassword)}
          aria-label="Campo de senha"
        />
        <Input
          label={t('Confirmar senha')}
          type="password"
          value={passwordConfirmation}
          onChange={e =>
            handleChangeInput(e.target.value, setPasswordConfirmation)
          }
          error={passwordError}
          helpText={
            passwordError ? t('Por favor, repita a senha corretamente') : ''
          }
          aria-label="Campo de confirmação de senha"
        />
        {password && <PasswordValidation password={password} />}
        <Button
          text={t('Avançar')}
          onClick={handleSubmit}
          disabled={passwordError}
          theme={{ type: 'primaryLivros', size: 'xLarge' }}
          fluid
          aria-label="Botão avançar"
          loading={submitLoading}
        />

        <Button
          text={t('Já lembrei minha senha')}
          onClick={() => history.push(LOGIN_PATH)}
          theme={{ type: 'ghost', size: 'xLarge' }}
          fluid
          aria-label="Botão voltar para login"
        />
      </InputWrapper>
    </Wrapper>
  )
}

export default RecoverPassword
